import { ChevronRight, Printer } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  mapPrintingSettings,
  toPrintingUpdateDTO,
} from './RestaurantSettingsMapper';
import RestaurantSettingsFormLayout from './RestaurantSettingsFormLayout';
import RestaurantSettingsToggle from './RestaurantSettingsToggle';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import useRestaurantSettingsForm from './useRestaurantSettingsForm';

interface Props {
  branchId: number;
}

const RestaurantPrintingSettingsForm = ({ branchId }: Props) => {
  const settings = useRestaurantSettingsForm(
    branchId,
    'printing',
    mapPrintingSettings,
    toPrintingUpdateDTO
  );
  const { form, updateField } = settings;

  return (
    <RestaurantSettingsFormLayout
      title="Impresión"
      description="Selecciona los documentos y copias que se imprimen durante la operación."
      loading={settings.loading}
      saving={settings.saving}
      hasData={Boolean(form)}
      alert={settings.alert}
      onCloseAlert={() => settings.setAlert(null)}
      onSave={settings.save}
      onRetry={settings.reload}
      help={<RestaurantFormHelp topic="settingsPrinting" variant="inline" />}
    >
      {form && (
        <>
          <div className="mb-2 rounded-xl border border-primary/15 bg-primary/5 p-4 sm:p-5 xl:col-span-2">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="rounded-lg bg-primary/10 p-2.5 text-primary">
                  <Printer className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-semibold text-black dark:text-white">
                    Equipos de impresión
                  </h3>
                  <p className="mt-1 text-sm text-bodydark2 dark:text-bodydark">
                    Registre y asigne las impresoras de comandas, recibos y etiquetas.
                  </p>
                </div>
              </div>

              <Link
                to="/restaurant/printers"
                className="inline-flex shrink-0 items-center justify-center gap-1 rounded-lg border border-primary px-4 py-2.5 text-sm font-medium text-primary transition hover:bg-primary hover:text-white"
              >
                Administrar impresoras
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <RestaurantSettingsToggle
            label="Imprimir comanda"
            description="Imprime la comanda que se envía al área de cocina."
            name="print-kitchen-ticket"
            checked={form.printKitchenTicket}
            onChange={() =>
              updateField('printKitchenTicket', !form.printKitchenTicket)
            }
          />
          <RestaurantSettingsToggle
            label="Imprimir ticket"
            description="Imprime el ticket de consumo para el cliente."
            name="print-customer-ticket"
            checked={form.printCustomerTicket}
            onChange={() =>
              updateField('printCustomerTicket', !form.printCustomerTicket)
            }
          />
          <RestaurantSettingsToggle
            label="Imprimir factura"
            description="Imprime la factura automáticamente después del pago."
            name="print-invoice-after-payment"
            checked={form.printInvoiceAfterPayment}
            onChange={() =>
              updateField(
                'printInvoiceAfterPayment',
                !form.printInvoiceAfterPayment
              )
            }
          />
          <RestaurantSettingsToggle
            label="Imprimir número de orden"
            description="Incluye el número de orden en los documentos impresos."
            name="print-order-number"
            checked={form.printOrderNumber}
            onChange={() => updateField('printOrderNumber', !form.printOrderNumber)}
          />
          <RestaurantSettingsToggle
            label="Imprimir copia para caja"
            description="Genera una copia adicional para el control de caja."
            name="print-cashier-copy"
            checked={form.printCashierCopy}
            onChange={() => updateField('printCashierCopy', !form.printCashierCopy)}
          />
          <RestaurantSettingsToggle
            label="Imprimir copia para cliente"
            description="Genera una copia adicional destinada al cliente."
            name="print-customer-copy"
            checked={form.printCustomerCopy}
            onChange={() =>
              updateField('printCustomerCopy', !form.printCustomerCopy)
            }
          />
        </>
      )}
    </RestaurantSettingsFormLayout>
  );
};

export default RestaurantPrintingSettingsForm;
